import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Heading,
  Card,
  Row,
  Column,
  Alert,
  OptionType,
  Box,
} from '@chargify/custom-ui-components';
import { useDeviceInfo } from '@chargify/device-provider';

import PaymentsProfilesHeader from './components/PaymentProfilesHeader';
import { PaymentProfileContainer } from '../../containers/PaymentProfileContainer';

import type { PaymentProfile } from '../../models/paymentProfile';

import { Wrapper, List } from './PaymentProfiles.styles';
import { PaymentProfilesSkeletonLoader } from './components/PaymentProfilesSkeletonLoader';

export type PaymentProfilesProps = {
  activePaymentProfile: PaymentProfile;
  paymentProfiles: PaymentProfile[];
  onSelectPaymentProfileOption: (option: OptionType, id: number) => void;
  onCreatePaymentProfile: (option: OptionType) => void;
  availablePaymentMethods: OptionType[];
  goBack: () => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

const PaymentProfiles = ({
  onRetry,
  onSelectPaymentProfileOption,
  onCreatePaymentProfile,
  availablePaymentMethods,
  goBack,
  isLoading,
  activePaymentProfile,
  paymentProfiles,
  isError,
}: PaymentProfilesProps) => {
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const { device } = useDeviceInfo();

  const portalContainerRef = useRef(null);
  const [paymentProfileDropdown, setPaymentProfileDropdown] =
    useState<number>(null);

  const componentSize = device === 'mobile' ? 'small' : 'regular';

  const hasPaymentProfiles =
    paymentProfiles?.length > 0 || activePaymentProfile;
  const isLoadingOrHasError = isLoading || isError;

  return (
    <>
      <Card>
        <div>
          <Card.Header>
            <PaymentsProfilesHeader
              dropdownOptions={availablePaymentMethods}
              onDropdownSelection={onCreatePaymentProfile}
            />
          </Card.Header>
        </div>
        <Card.Body>
          {isLoading && <PaymentProfilesSkeletonLoader />}
          {!hasPaymentProfiles && !isLoadingOrHasError && (
            <Alert
              color="danger"
              title={t('payment_profile.no_any_payment_methods')}
            >
              <Alert.TextMessage>
                {t('payment_profile.add_new_payment_method_to_proceed')}
              </Alert.TextMessage>
            </Alert>
          )}
          {isError && (
            <Alert
              title={t('payment_profile.unexpected_error_title')}
              color="danger"
              buttonLabel={t('payment_profile.try_again')}
              onClick={onRetry}
            >
              <Alert.TextMessage>
                {t('payment_profile.unexpected_error_message')}
              </Alert.TextMessage>
            </Alert>
          )}
          {hasPaymentProfiles && (
            <>
              <Row data-testid="active-payment-method-row">
                <Column span={[24, 24, 6]}>
                  <Heading variant="h4">
                    {t('payment_profile.active_payment_method')}
                  </Heading>
                </Column>
                <Column span={[24, 24, 18]}>
                  {activePaymentProfile ? (
                    <PaymentProfileContainer
                      paymentProfile={activePaymentProfile}
                      componentSize={componentSize}
                      portalContainerRef={portalContainerRef}
                      isDropdownOpen={
                        paymentProfileDropdown === activePaymentProfile.id
                      }
                      onSelectOption={onSelectPaymentProfileOption}
                      onToggleDropdown={(id) => setPaymentProfileDropdown(id)}
                    />
                  ) : (
                    <Wrapper>
                      <Alert color="primary">
                        <Alert.TextMessage>
                          {t('payment_profile.no_saved_payment_methods')}
                        </Alert.TextMessage>
                      </Alert>
                    </Wrapper>
                  )}
                </Column>
              </Row>
              {paymentProfiles?.length > 0 && (
                <Box marginTop={8}>
                  <Row rowGap={16} data-testid="other-payment-methods-row">
                    <Column span={[24, 24, 6]}>
                      <Heading variant="h4">
                        {t('payment_profile.other_payment_methods')}
                      </Heading>
                    </Column>
                    <Column span={[24, 24, 18]}>
                      <List gridGap={8} data-testid="credit-card-list">
                        {paymentProfiles.map((paymentProfile) => (
                          <div
                            key={paymentProfile.id}
                            data-testid="credit-card-item"
                          >
                            <PaymentProfileContainer
                              paymentProfile={paymentProfile}
                              componentSize={componentSize}
                              portalContainerRef={portalContainerRef}
                              isDropdownOpen={
                                paymentProfileDropdown === paymentProfile.id
                              }
                              onSelectOption={onSelectPaymentProfileOption}
                              onToggleDropdown={(id) =>
                                setPaymentProfileDropdown(id)
                              }
                            />
                          </div>
                        ))}
                      </List>
                    </Column>
                  </Row>
                </Box>
              )}
            </>
          )}
        </Card.Body>
        <Card.Footer>
          <Button
            color="secondary"
            label={t('back_to_subscriptions')}
            onClick={goBack}
          />
        </Card.Footer>
      </Card>
      <div
        ref={portalContainerRef}
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
    </>
  );
};
export default PaymentProfiles;
